import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MagnifyingGlass, Plus, BookOpen, List, X, FileText, Funnel, ArrowsOutSimple, ArrowsInSimple, Lock, Envelope, ArrowSquareUpRight, Sparkle } from '@phosphor-icons/react';
import { useAuth, isAdmin, isSimpleMenu } from '@/auth/AuthContext';
import { knowledgeApi, buildTree } from './knowledgeApi';

// 技術メニューから移した AI の道具（管理者のみ）。
const AI_TOOLS = [
  { to: '/engineering/ai-digest', label: '技術ダイジェスト', icon: Envelope, testid: 'kb-ai-tool-digest' },
  { to: '/engineering/kb-export', label: 'ナレッジ出力', icon: ArrowSquareUpRight, testid: 'kb-ai-tool-export' },
  { to: '/engineering/ai-feedback', label: '読み取りフィードバック', icon: Sparkle, testid: 'kb-ai-tool-feedback' },
];
import { PAGE_TEMPLATES, templateBody } from './pageTemplates';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import PageTree from './PageTree';
import './knowledgeDocument.css';

// Batch 4 · iter_154 — sidebar-width persistence.
const SIDEBAR_KEY = 'sensbey_knowledge_sidebar_width';
const SIDEBAR_MIN = 200;
const SIDEBAR_MAX = 480;
const SIDEBAR_DEFAULT = 256;
function readStoredSidebarWidth() {
  try {
    const v = parseInt(localStorage.getItem(SIDEBAR_KEY) || '', 10);
    if (Number.isFinite(v) && v >= SIDEBAR_MIN && v <= SIDEBAR_MAX) return v;
  } catch { /* noop */ }
  return SIDEBAR_DEFAULT;
}

// 検索語をハイライト
function highlight(text, term) {
  const t = (term || '').trim();
  if (!text || !t) return text;
  try {
    const parts = String(text).split(new RegExp(`(${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
    return parts.map((p, i) => (p.toLowerCase() === t.toLowerCase()
      ? <mark key={`${i}-${p}`} className="rounded bg-yellow-200 px-0.5 text-inherit">{p}</mark> : p));
  } catch { return text; }
}

export default function KnowledgeLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const simple = isSimpleMenu(user);
  const { id: activeId } = useParams();
  const [flat, setFlat] = useState([]);
  const [meta, setMeta] = useState({ categories: [], tags: [], can_edit: false });
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchWrap = useRef(null);
  const [catFilter, setCatFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selIndex, setSelIndex] = useState(0);
  const [treeCmd, setTreeCmd] = useState(null);
  // Batch 4 — resizable sidebar
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, w: SIDEBAR_DEFAULT });

  // Drag-handle mouse handlers. `mouseup` also flushes the current width
  // to localStorage so the preference survives reload / new-tab.
  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const dx = e.clientX - resizeStartRef.current.x;
      let next = resizeStartRef.current.w + dx;
      if (next < SIDEBAR_MIN) next = SIDEBAR_MIN;
      if (next > SIDEBAR_MAX) next = SIDEBAR_MAX;
      setSidebarWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      try { localStorage.setItem(SIDEBAR_KEY, String(sidebarWidth)); } catch { /* noop */ }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // Prevent text-selection glitches during drag
    const prevUS = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUS;
    };
  }, [resizing, sidebarWidth]);

  const startResize = (e) => {
    resizeStartRef.current = { x: e.clientX, w: sidebarWidth };
    setResizing(true);
  };
  const resetSidebar = () => {
    setSidebarWidth(SIDEBAR_DEFAULT);
    try { localStorage.setItem(SIDEBAR_KEY, String(SIDEBAR_DEFAULT)); } catch { /* noop */ }
  };

  const reloadTree = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([knowledgeApi.tree(), knowledgeApi.meta()]);
      setFlat(t); setMeta(m);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { reloadTree(); }, [reloadTree]);

  // debounced server-side search (+ category/tag filters)
  useEffect(() => {
    const term = q.trim();
    if (!term && !catFilter && !tagFilter) { setResults(null); return undefined; }
    setSearching(true);
    const tm = setTimeout(async () => {
      try { setResults(await knowledgeApi.search(term, { category: catFilter, tag: tagFilter })); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(tm);
  }, [q, catFilter, tagFilter]);

  useEffect(() => { setSelIndex(0); }, [results]);

  const onSearchKey = (e) => {
    if (results === null || !results.length) {
      if (e.key === 'Escape') { setQ(''); setResults(null); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelIndex((i) => (i + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelIndex((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = results[selIndex]; if (r) openResult(r); }
    else if (e.key === 'Escape') { setQ(''); setResults(null); }
  };

  useEffect(() => {
    const onDoc = (e) => { if (searchWrap.current && !searchWrap.current.contains(e.target)) setResults(null); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const newPage = useCallback(async (parentId = null, tplKey = 'blank') => {
    try {
      const tpl = PAGE_TEMPLATES.find((x) => x.key === tplKey) || PAGE_TEMPLATES[0];
      const a = await knowledgeApi.create({ ...templateBody(tpl), parent_id: parentId, status: 'draft' });
      await reloadTree();
      navigate(`/knowledge/p/${a.slug || a.id}/edit`);
      setMobileOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'ページを作成できませんでした'); }
  }, [navigate, reloadTree]);

  const tree = buildTree(flat);
  const canEdit = !!meta.can_edit;
  const activeFilters = (catFilter ? 1 : 0) + (tagFilter ? 1 : 0);

  const reorder = useCallback(async (dragId, targetId, placement) => {
    try {
      await knowledgeApi.reposition(dragId, { target_id: targetId, placement });
      await reloadTree();
    } catch (e) { toast.error(e?.response?.data?.detail || 'ページを移動できませんでした'); }
  }, [reloadTree]);

  const openResult = (r) => { setQ(''); setResults(null); navigate(`/knowledge/p/${r.slug || r.id}`); };

  const Sidebar = (
    <div className="flex h-full w-full min-w-0 flex-col">
      <div className="flex items-center justify-between px-3 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-2 text-zinc-800">
          <BookOpen size={18} weight="duotone" className="text-teal-700" />
          <span className="font-semibold text-sm">ナレッジ</span>
        </div>
        <button type="button" className="md:hidden text-zinc-400" onClick={() => setMobileOpen(false)}><X size={18} /></button>
      </div>
      <div className="flex items-center gap-0.5 px-2 pt-2">
        <button type="button" title="すべて展開" data-testid="kb-expand-all" onClick={() => setTreeCmd({ type: 'expand', n: Date.now() })}
          className="inline-flex min-h-[32px] items-center gap-1 rounded px-1.5 py-1 text-[11px] text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"><ArrowsOutSimple size={13} />すべて展開</button>
        <button type="button" title="すべて折りたたむ" data-testid="kb-collapse-all" onClick={() => setTreeCmd({ type: 'collapse', n: Date.now() })}
          className="inline-flex min-h-[32px] items-center gap-1 rounded px-1.5 py-1 text-[11px] text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"><ArrowsInSimple size={13} />折りたたむ</button>
      </div>
      {canEdit && (
        <div className="px-2 pt-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="kb-new-page-btn"
                className="flex w-full items-center gap-1.5 rounded-md bg-teal-700 px-3 py-2 text-sm font-medium text-white hover:bg-teal-800 transition-colors">
                <Plus size={15} weight="bold" />新規ページ
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {PAGE_TEMPLATES.map((tpl) => (
                <DropdownMenuItem key={tpl.key} data-testid={`kb-new-page-${tpl.key}`}
                  onClick={() => newPage(null, tpl.key)} className="flex-col items-start gap-0.5">
                  <span className="text-sm text-zinc-900">{tpl.label}</span>
                  <span className="text-[11px] text-zinc-500">{tpl.hint}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-1.5 py-2">
        <PageTree nodes={tree} activeId={activeId} canEdit={canEdit} onAddChild={(pid) => newPage(pid)} onReorder={reorder} command={treeCmd} />
      </div>
      {/* AIの道具（管理者のみ）— 2026-06 シンプルメニュー。技術メニューから
          ここへ移した。OFF のときは技術メニューにも今までどおり出る。 */}
      {simple && admin && (
        <div className="border-t border-zinc-200 px-2 py-2" data-testid="kb-ai-tools">
          <p className="px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            AIの道具（管理者）
          </p>
          {AI_TOOLS.map((tool) => (
            <NavLink key={tool.to} to={tool.to} data-testid={tool.testid}
              className="flex items-center gap-2 rounded-md px-1.5 py-1.5 text-[12.5px] text-zinc-700 hover:bg-zinc-100">
              <tool.icon size={14} weight="duotone" className="shrink-0 text-zinc-500" />
              <span className="truncate">{tool.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="knowledge-shell mx-auto max-w-[1600px]">
      <div className="knowledge-shell-card flex gap-0 rounded-xl border border-zinc-200 bg-white overflow-hidden min-h-[78vh]">
        {/* sidebar (desktop) — Batch 4: resizable via the handle on the
            right edge. Width persists in localStorage between sessions. */}
        <aside
          className="kb-no-print hidden md:flex shrink-0 border-r border-zinc-200 bg-zinc-50/50 relative overflow-hidden"
          style={{ width: `${sidebarWidth}px` }}
          data-testid="kb-sidebar"
        >
          {Sidebar}
          {/* Resize handle — 6px wide, absolutely positioned on the right
              border. Double-click resets to default. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="サイドバーの幅を調整"
            data-testid="kb-sidebar-resize-handle"
            onMouseDown={startResize}
            onDoubleClick={resetSidebar}
            title={`ドラッグして幅を調整 · ダブルクリックで既定 (${SIDEBAR_DEFAULT}px)`}
            className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize group ${resizing ? 'bg-teal-400/40' : 'hover:bg-teal-300/40'} transition-colors`}
          >
            <span className="pointer-events-none absolute top-1/2 -translate-y-1/2 -right-1 h-6 w-1 rounded-full bg-zinc-300 group-hover:bg-teal-500 transition-colors" />
          </div>
        </aside>

        {/* main */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* top search bar */}
          <div className="kb-no-print border-b border-zinc-200 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <button type="button" aria-label="ページ一覧を開く" title="ページ一覧を開く" className="md:hidden inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100" onClick={() => setMobileOpen(true)} data-testid="kb-mobile-menu"><List size={20} /></button>
              <div ref={searchWrap} className="relative flex-1 max-w-xl">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
                  data-testid="kb-search-input"
                  placeholder="ナレッジを検索…"
                  className="w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-9 pr-8 py-2 text-sm outline-none focus:border-teal-500 focus:bg-white"
                />
                {q && (
                  <button type="button" onClick={() => { setQ(''); setResults(null); }} data-testid="kb-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500"><X size={14} /></button>
                )}
                {results !== null && (
                  <div className="absolute z-30 mt-1 w-full rounded-lg border border-zinc-200 bg-white shadow-xl max-h-96 overflow-y-auto" data-testid="kb-search-results">
                    {searching && <p className="px-3 py-3 text-xs text-zinc-400">検索中…</p>}
                    {!searching && results.length === 0 && <p className="px-3 py-3 text-xs text-zinc-400" data-testid="kb-search-empty">該当するページがありません</p>}
                    {results.map((r, i) => (
                      <button key={r.id} type="button" onClick={() => openResult(r)} onMouseEnter={() => setSelIndex(i)} data-testid={`kb-search-result-${r.id}`}
                        className={`flex w-full items-start gap-2 px-3 py-2 text-left ${i === selIndex ? 'bg-teal-50' : 'hover:bg-zinc-50'}`}>
                        <span className="shrink-0 w-4 text-center">{r.icon || <FileText size={14} className="inline text-zinc-400" />}</span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-sm text-zinc-800">{highlight(r.title, q)}
                            {r.locked && <span className="inline-flex items-center gap-0.5 rounded bg-zinc-100 px-1 text-[9px] text-zinc-500"><Lock size={9} weight="fill" />限定</span>}
                            {r.status === 'draft' && <span className="rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-700">下書き</span>}
                            {r.category && <span className="rounded bg-zinc-100 px-1 text-[9px] text-zinc-500">{r.category}</span>}</span>
                          {r.snippet && <span className="block truncate text-xs text-zinc-400">{highlight(r.snippet, q)}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {(meta.categories?.length > 0 || meta.tags?.length > 0) && (
                <button type="button" onClick={() => setShowFilters((v) => !v)} data-testid="kb-filter-toggle"
                  aria-label={activeFilters ? `絞り込み条件（${activeFilters}件適用中）` : '絞り込み条件'}
                  aria-expanded={showFilters}
                  title={activeFilters ? `絞り込み条件（${activeFilters}件適用中）` : '絞り込み条件'}
                  className={`relative inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-sm ${activeFilters ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}>
                  <Funnel size={16} weight={activeFilters ? 'fill' : 'regular'} />
                  {activeFilters > 0 && <span className="text-xs font-medium">{activeFilters}</span>}
                </button>
              )}
            </div>
            {showFilters && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="kb-filter-panel">
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="kb-filter-category"
                  className="rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm text-zinc-700 outline-none focus:border-teal-500">
                  <option value="">カテゴリ: すべて</option>
                  {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="kb-filter-tag"
                  className="rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm text-zinc-700 outline-none focus:border-teal-500">
                  <option value="">タグ: すべて</option>
                  {(meta.tags || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {activeFilters > 0 && (
                  <button type="button" onClick={() => { setCatFilter(''); setTagFilter(''); }} data-testid="kb-filter-clear"
                    className="text-xs text-zinc-400 hover:text-zinc-600">絞り込みをクリア</button>
                )}
              </div>
            )}
          </div>

          <div className="knowledge-main-scroll flex-1 overflow-y-auto">
            <Outlet context={{ tree, flat, reloadTree, meta, canEdit, newPage }} />
          </div>
        </div>
      </div>

      {/* mobile sidebar drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex kb-no-print" data-testid="kb-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 max-w-[80%] bg-white h-full shadow-xl">{Sidebar}</div>
        </div>
      )}
    </div>
  );
}
